import { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../lib/apiClient';

export function ResetPasswordPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!token) {
      setError('Reset token missing. Please request a new reset link.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setStatus('loading');
    try {
      await resetPassword(token, password);
      setStatus('success');
    } catch (err) {
      setError('Unable to reset password. Please request a new link.');
      setStatus('idle');
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-8">
      <div className="space-y-3 text-center">
        <h1 className="text-3xl font-semibold text-foreground">Choose a new password</h1>
        <p className="text-sm text-muted-foreground">
          Set a fresh password for your InspectAI account.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="glass-card space-y-4 p-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground" htmlFor="password">
            New password
          </label>
          <input
            id="password"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="focus-ring w-full rounded-xl border border-white/20 bg-white/70 px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground dark:bg-white/10"
            placeholder="Create a strong password"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground" htmlFor="confirm-password">
            Confirm password
          </label>
          <input
            id="confirm-password"
            type="password"
            required
            minLength={8}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            className="focus-ring w-full rounded-xl border border-white/20 bg-white/70 px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground dark:bg-white/10"
            placeholder="Repeat your new password"
          />
        </div>

        {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        {status === 'success' && (
          <p className="rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
            Password updated. You can now log in.
          </p>
        )}

        <button
          type="submit"
          disabled={status === 'loading' || status === 'success'}
          className="focus-ring inline-flex w-full items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-primary/30 transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === 'loading' ? 'Updating…' : status === 'success' ? 'Updated' : 'Update password'}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        Back to{' '}
        <Link to="/login" className="text-primary hover:underline">
          log in
        </Link>
        .
      </p>
    </div>
  );
}
